import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Users, Plus, X, Minus, ChevronRight, ShoppingBag, Loader2, Check, Utensils, Clipboard, Share2, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function GroupOrder({ businessId, businessName, onClose }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showVendorView, setShowVendorView] = useState(false);
  const [vendorData, setVendorData] = useState(null);
  const [personName, setPersonName] = useState("");
  const [itemName, setItemName] = useState("");
  const [chips, setChips] = useState([]);
  const [chipInput, setChipInput] = useState("");
  const [showQR, setShowQR] = useState(false);

  const fetchSession = useCallback(async (sid) => {
    try {
      const res = await axios.get(`${API}/group-order/${sid}`);
      setSession(res.data);
    } catch { /* ignore */ }
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/group-order/create`, { business_id: businessId });
      toast.success("Group order started!");
      await fetchSession(res.data.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create group order");
    } finally { setCreating(false); }
  };

  const handleAddItem = async () => {
    if (!personName.trim() || !itemName.trim()) {
      toast.error("Name and item are required");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/group-order/add-item`, {
        session_id: session.session_id,
        person_name: personName.trim(),
        item_name: itemName.trim(),
        customizations: chips,
      });
      toast.success(`Added ${itemName} for ${personName}`);
      setPersonName("");
      setItemName("");
      setChips([]);
      await fetchSession(session.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add item");
    } finally { setAdding(false); }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await axios.post(`${API}/group-order/remove-item`, {
        session_id: session.session_id,
        item_id: itemId,
      });
      await fetchSession(session.session_id);
    } catch (err) {
      toast.error("Could not remove item");
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API}/group-order/finalize`, {
        session_id: session.session_id,
      });
      toast.success("Order finalized! Show vendor view.");
      await fetchSession(session.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finalize");
    } finally { setFinalizing(false); }
  };

  const handleShowVendor = async () => {
    try {
      const res = await axios.get(`${API}/group-order/vendor-view/${session.session_id}`);
      setVendorData(res.data);
      setShowVendorView(true);
    } catch (err) {
      toast.error("Could not load vendor view");
    }
  };

  const addChip = () => {
    const val = chipInput.trim();
    if (val && !chips.includes(val)) {
      setChips(c => [...c, val]);
      setChipInput("");
    }
  };

  const removeChip = (chip) => setChips(c => c.filter(x => x !== chip));

  const handleShareCode = async () => {
    const code = session?.session_id || "";
    const msg = `Join our group order at ${businessName}! Use code ${code} on BirthdayDeaLight. When Phase 3 launches friends will be able to add their own orders — for now the leader adds everyone's order manually.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Group Order at ${businessName}`, text: msg });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(msg);
        toast.success("Order code copied to clipboard!");
      } catch { toast.error("Could not share"); }
    }
  };

  // Vendor view
  if (showVendorView && vendorData) {
    return (
      <div className="fixed inset-0 z-50 bg-[#1C1C1E] flex flex-col" data-testid="group-order-vendor-view">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black text-white">{vendorData.business_name}</h1>
            <p className="text-xs text-white/50">{vendorData.total_people} people, {vendorData.total_items} items</p>
          </div>
          <button onClick={() => setShowVendorView(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-testid="close-vendor-view">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {vendorData.consolidated.map((group, i) => (
            <div key={i} className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid={`vendor-item-group-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-black text-[#F59E0B]">{group.item_name}</h2>
                <span className="text-2xl font-black text-white bg-[#F59E0B] w-10 h-10 rounded-full flex items-center justify-center">
                  {group.quantity}
                </span>
              </div>
              <div className="space-y-1.5">
                {group.orders.map((order, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <span className="text-xs text-white/60 min-w-[60px]">{order.person_name}</span>
                    {order.customizations?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {order.customizations.map((c, k) => (
                          <span key={k} className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-white/10">
          <p className="text-[10px] text-white/30 text-center">Order #{vendorData.session_id} by {vendorData.leader_name}</p>
        </div>
      </div>
    );
  }

  // No session yet
  if (!session) {
    return (
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid="group-order-start">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">Group Order</p>
            <p className="text-[10px] text-slate-500">Collect everyone's order on one device</p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="w-full py-2.5 rounded-lg bg-blue-500 text-white font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition disabled:opacity-50"
          data-testid="start-group-order-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Start Group Order
        </button>
      </div>
    );
  }

  const isOpen = session.status === "open";
  const items = session.items || [];

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-testid="group-order-panel">
      {/* Header */}
      <div className="bg-blue-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-white" />
          <span className="text-sm font-bold text-white">Group Order</span>
          <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full">{items.length} items</span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          isOpen ? "bg-green-500/20 text-green-200" : "bg-amber-500/20 text-amber-200"
        }`}>
          {isOpen ? "OPEN" : "FINALIZED"}
        </span>
      </div>

      {/* Session code + Share / QR buttons */}
      {session?.session_id && (
        <div className="px-4 py-3 border-b border-slate-100 space-y-2" data-testid="group-order-share-section">
          <p className="text-[10px] text-slate-400 text-center">Session Code</p>
          <p className="text-lg font-black text-slate-800 text-center tracking-widest" data-testid="group-session-code">{session.session_id}</p>
          <button
            onClick={handleShareCode}
            className="w-full py-2 rounded-lg border-2 border-[#F59E0B] text-[#F59E0B] font-bold text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition hover:bg-amber-50"
            data-testid="share-order-code-btn"
          >
            <Share2 className="w-3.5 h-3.5" /> Share Order Code
          </button>
          <button
            onClick={() => setShowQR(true)}
            className="w-full py-2 rounded-lg border border-slate-300 text-slate-600 font-bold text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition hover:bg-slate-50"
            data-testid="show-qr-code-btn"
          >
            <QrCode className="w-3.5 h-3.5" /> Show QR Code
          </button>
        </div>
      )}

      {/* QR Code Bottom Sheet */}
      {showQR && session?.session_id && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end" data-testid="qr-code-overlay" onClick={() => setShowQR(false)}>
          <div className="w-full max-w-md mx-auto bg-white rounded-t-2xl p-6 pb-10 animate-in slide-in-from-bottom" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800">Scan to see session code</h3>
              <button onClick={() => setShowQR(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="qr-close-btn">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl border border-slate-200" data-testid="qr-code-container">
                <QRCodeSVG value={`BDEALIGHT-${session.session_id}`} size={280} level="M" />
              </div>
              <p className="text-2xl font-black text-slate-800 mt-4 tracking-widest" data-testid="qr-session-code-text">{session.session_id}</p>
              <p className="text-[10px] text-slate-400 mt-2 text-center">Friends can scan this to see the session code</p>
            </div>
          </div>
        </div>
      )}

      {/* Items list */}
      {items.length > 0 && (
        <div className="max-h-48 overflow-auto divide-y divide-slate-100">
          {items.map((item, i) => (
            <div key={item.item_id} className="flex items-center gap-2 px-4 py-2" data-testid={`group-item-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{item.item_name}</p>
                <p className="text-[10px] text-slate-500">{item.person_name}</p>
                {item.customizations?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {item.customizations.map((c, j) => (
                      <span key={j} className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                )}
              </div>
              {isOpen && (
                <button onClick={() => handleRemoveItem(item.item_id)} className="text-slate-300 hover:text-red-400 transition" data-testid={`remove-item-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add item form */}
      {isOpen && (
        <div className="p-3 border-t border-slate-100 space-y-2" data-testid="add-item-form">
          <div className="flex gap-2">
            <input
              value={personName}
              onChange={e => setPersonName(e.target.value)}
              placeholder="Name"
              className="flex-1 bg-white text-slate-800 text-xs rounded-lg px-3 py-2 border border-slate-200 focus:border-blue-400 outline-none"
              data-testid="group-person-name"
            />
            <input
              value={itemName}
              onChange={e => setItemName(e.target.value)}
              placeholder="Item"
              className="flex-1 bg-white text-slate-800 text-xs rounded-lg px-3 py-2 border border-slate-200 focus:border-blue-400 outline-none"
              data-testid="group-item-name"
            />
          </div>

          {/* Customization chips */}
          <div className="flex flex-wrap gap-1">
            {chips.map(c => (
              <span key={c} className="flex items-center gap-1 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {c} <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => removeChip(c)} />
              </span>
            ))}
            <div className="flex items-center gap-1">
              <input
                value={chipInput}
                onChange={e => setChipInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addChip()}
                placeholder="+ customize"
                className="bg-transparent text-[10px] text-slate-500 w-20 outline-none"
                data-testid="group-chip-input"
              />
            </div>
          </div>

          <button
            onClick={handleAddItem}
            disabled={adding || !personName.trim() || !itemName.trim()}
            className="w-full py-2 rounded-lg bg-blue-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition"
            data-testid="add-to-group-btn"
          >
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add to Order
          </button>
        </div>
      )}

      {/* Finalize / Vendor View */}
      <div className="p-3 border-t border-slate-100 flex gap-2">
        {isOpen ? (
          <button
            onClick={handleFinalize}
            disabled={finalizing || items.length === 0}
            className="flex-1 py-2.5 rounded-lg bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition"
            data-testid="finalize-order-btn"
          >
            {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Finalize Order
          </button>
        ) : (
          <button
            onClick={handleShowVendor}
            className="flex-1 py-2.5 rounded-lg bg-[#1C1C1E] text-white font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
            data-testid="show-vendor-view-btn"
          >
            <Clipboard className="w-4 h-4" /> Show to Vendor
          </button>
        )}
      </div>
    </div>
  );
}
